// Luxe · Design Template Gallery (Prompt 4E)
// Opens as a modal from the DESIGN stage "Start a design" empty state.
import { useEffect, useMemo, useState } from "react";
import api, { formatErr } from "@/lib/api";
import { X } from "lucide-react";
import { toast } from "sonner";

const FILTERS = [
  { key: "all",         label: "All" },
  { key: "Modern",      label: "Modern" },
  { key: "Coastal",     label: "Coastal" },
  { key: "Fireplace",   label: "Fireplace" },
  { key: "Minimal",     label: "Minimal" },
  { key: "Joinery",     label: "Joinery" },
  { key: "Feature Wall", label: "Feature Wall" },
  { key: "AV",          label: "AV" },
];

function compatibilityFor(t, wallW) {
  if (!wallW) return "unknown";
  const mn = t.recommended_min_width_mm, mx = t.recommended_max_width_mm;
  if (wallW >= mn && wallW <= mx) return "good_fit";
  const span = (mx - mn) || 1;
  if (wallW < mn) return (mn - wallW) <= span * 0.15 ? "adjustment_required" : "not_recommended";
  return (wallW - mx) <= span * 0.15 ? "adjustment_required" : "not_recommended";
}

const COMPAT_COPY = {
  good_fit: { label: "Good fit", tone: "text-emerald-700" },
  adjustment_required: { label: "Adjustment required", tone: "text-amber-600" },
  not_recommended: { label: "Not recommended", tone: "text-red-600" },
  unknown: { label: "Compatibility unknown", tone: "text-muted-foreground" },
};

function ThumbnailPreview({ template, size = "card" }) {
  // Simple schematic isometric-ish preview derived from the
  // template's own scene definition. Deterministic — same input,
  // same output — so gallery cards look intentional.
  const items = template?.scene_definition?.items || [];
  const wallW = template.default_wall_width_mm;
  const wallH = template.default_wall_height_mm;
  const pad = 12;
  const vbW = 400, vbH = Math.round(400 * (wallH / wallW));
  const scale = (vbW - pad * 2) / wallW;
  const boxes = items.map((it, idx) => {
    const w = it.width_mm ?? Math.round(wallW * (it.width_frac || 0.3));
    const h = it.height_mm ?? Math.round(wallH * (it.height_frac || 0.3));
    let x = wallW * (it.rel_x || 0);
    if (it.anchor === "centre") x -= w / 2;
    if (it.anchor === "right") x -= w;
    const y = wallH - (wallH * (it.rel_y || 0)) - h;
    const fill = {
      tv: "#0f0f0f", fireplace: "#7a2c2c", floating_cabinet: "#c9b39b",
      tall_cabinet: "#a08a76", shelf: "#b8a48b", niche: "#e6e0d5",
      soundbar: "#1a1a1a", led_strip: "#f2c86b", feature_panel: "#e5ded1",
      timber_panel: "#c9a273",
    }[it.item_type] || "#d5cdbf";
    return { x: pad + x * scale, y: pad + y * scale,
             w: Math.max(2, w * scale), h: Math.max(2, h * scale), fill, key: idx };
  });
  return (
    <svg viewBox={`0 0 ${vbW} ${vbH}`} className="w-full h-full" data-testid={`tpl-preview-${template.template_key}`}>
      <rect x="0" y="0" width={vbW} height={vbH} fill="#f4efe6"/>
      <rect x={pad} y={pad} width={vbW - pad * 2} height={vbH - pad * 2} fill="#ede4d3" stroke="#c9bfa9"/>
      {boxes.map((b) => (
        <rect key={b.key} x={b.x} y={b.y} width={b.w} height={b.h} fill={b.fill} opacity={0.9}/>
      ))}
    </svg>
  );
}

export default function DesignTemplateGallery({ project, onInstantiated, onClose }) {
  const [templates, setTemplates] = useState([]);
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/design-templates");
        setTemplates(data);
      } catch (e) { toast.error(formatErr(e)); }
    })();
  }, []);

  const visible = useMemo(() => {
    if (filter === "all") return templates;
    if (filter === "Fireplace") return templates.filter((t) => t.has_fireplace);
    return templates.filter((t) => t.category === filter);
  }, [templates, filter]);

  const hasExistingDesign = ((project?.schematic?.items || []).length > 0);

  const instantiate = async ({ save_current_first }) => {
    if (!selected) return;
    setBusy(true);
    try {
      const { data } = await api.post(
        `/projects/${project.id}/instantiate-template`,
        { template_id: selected.id, save_current_first },
      );
      toast.success("Design added — adjust it to suit your project.");
      onInstantiated && onInstantiated(data);
      onClose && onClose();
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setBusy(false);
      setConfirming(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
         data-testid="template-gallery-modal">
      <div className="bg-card max-w-6xl w-full max-h-[90vh] overflow-y-auto border border-border">
        <div className="p-8 pb-4 flex items-start justify-between border-b border-border">
          <div>
            <div className="overline">Design Templates</div>
            <h2 className="font-serif font-light mt-2" style={{ fontSize: "36px", lineHeight: 1.05 }}>
              Start from a curated design
            </h2>
            <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
              Pick a premium starting point and Subtend Studio will adapt it to your project's wall dimensions.
            </p>
          </div>
          <button data-testid="btn-close-template-gallery" onClick={onClose}>
            <X strokeWidth={1.5}/>
          </button>
        </div>

        {!selected && (
          <>
            <div className="px-8 py-4 flex gap-2 flex-wrap sticky top-0 bg-card z-10 border-b border-border">
              {FILTERS.map((f) => (
                <button key={f.key}
                        data-testid={`tpl-filter-${f.key}`}
                        onClick={() => setFilter(f.key)}
                        className={`text-xs px-3 py-1.5 border ${filter === f.key ? "border-foreground bg-foreground text-background" : "border-border text-muted-foreground hover:text-foreground"}`}>
                  {f.label}
                </button>
              ))}
            </div>

            <div className="p-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {visible.map((t) => {
                const compat = compatibilityFor(t, project?.wall_width_mm);
                return (
                  <button key={t.id} data-testid={`tpl-card-${t.template_key}`}
                          onClick={() => setSelected(t)}
                          className="text-left border border-border bg-background hover:shadow-md transition-shadow overflow-hidden">
                    <div className="aspect-[4/3] bg-muted">
                      <ThumbnailPreview template={t}/>
                    </div>
                    <div className="p-4">
                      <div className="overline text-[10px]">{t.style}</div>
                      <div className="font-serif text-xl mt-1">{t.name}</div>
                      <div className="text-xs text-muted-foreground mt-2 line-clamp-2">{t.description}</div>
                      <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] mt-3 text-muted-foreground">
                        <span>Wall {t.recommended_min_width_mm}–{t.recommended_max_width_mm} mm</span>
                        <span>TV {t.recommended_tv_size_inches}</span>
                        {t.has_fireplace && <span>Fireplace</span>}
                        <span>{t.complexity}</span>
                      </div>
                      <div className={`text-[11px] mt-2 ${COMPAT_COPY[compat].tone}`}
                           data-testid={`tpl-compat-${t.template_key}`}>
                        {COMPAT_COPY[compat].label}
                      </div>
                    </div>
                  </button>
                );
              })}
              {visible.length === 0 && (
                <div className="col-span-full text-center text-sm text-muted-foreground py-16">
                  No templates match this filter yet.
                </div>
              )}
            </div>
          </>
        )}

        {selected && (
          <div className="p-8" data-testid="tpl-preview-panel">
            <button data-testid="btn-tpl-back" onClick={() => setSelected(null)} className="btn-outline text-xs mb-6">
              ← Back to designs
            </button>
            <div className="grid lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)] gap-8">
              <div className="aspect-[4/3] border border-border bg-muted">
                <ThumbnailPreview template={selected} size="preview"/>
              </div>
              <div>
                <div className="overline">{selected.style}</div>
                <h3 className="font-serif font-light mt-2" style={{ fontSize: "32px", lineHeight: 1.05 }}>{selected.name}</h3>
                <p className="text-sm text-muted-foreground mt-3">{selected.description}</p>
                <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-xs mt-6">
                  <dt className="text-muted-foreground">Recommended wall</dt>
                  <dd>{selected.recommended_min_width_mm}–{selected.recommended_max_width_mm} mm</dd>
                  <dt className="text-muted-foreground">Your wall</dt>
                  <dd>{project?.wall_width_mm || "—"} mm</dd>
                  <dt className="text-muted-foreground">TV</dt>
                  <dd>{selected.recommended_tv_size_inches}</dd>
                  <dt className="text-muted-foreground">Fireplace</dt>
                  <dd>{selected.has_fireplace ? "Yes" : "No"}</dd>
                  <dt className="text-muted-foreground">Materials</dt>
                  <dd>{selected.primary_materials.join(" · ")}</dd>
                  <dt className="text-muted-foreground">Complexity</dt>
                  <dd>{selected.complexity}</dd>
                  <dt className="text-muted-foreground">Compatibility</dt>
                  <dd className={COMPAT_COPY[compatibilityFor(selected, project?.wall_width_mm)].tone}>
                    {COMPAT_COPY[compatibilityFor(selected, project?.wall_width_mm)].label}
                  </dd>
                </dl>

                {!confirming && (
                  <button data-testid="btn-use-this-design"
                          disabled={busy}
                          onClick={() => hasExistingDesign ? setConfirming(true) : instantiate({ save_current_first: false })}
                          className="btn-primary mt-8">
                    {busy ? "Adding…" : "Use this design"}
                  </button>
                )}

                {confirming && (
                  <div className="mt-8 space-y-3 border-t border-border pt-6" data-testid="tpl-confirm-replace">
                    <p className="text-sm">
                      Your project already has a design. Save it first, or replace it?
                    </p>
                    <div className="flex gap-2 flex-wrap">
                      <button data-testid="btn-save-and-use"
                              disabled={busy}
                              onClick={() => instantiate({ save_current_first: true })}
                              className="btn-primary text-sm">
                        Save current & use template
                      </button>
                      <button data-testid="btn-replace-existing"
                              disabled={busy}
                              onClick={() => instantiate({ save_current_first: false })}
                              className="btn-outline text-sm">
                        Replace existing design
                      </button>
                      <button data-testid="btn-tpl-cancel"
                              onClick={() => setConfirming(false)}
                              className="btn-outline text-sm">
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
